import React from "react";
import { replaceCollection, replaceRecord } from "../components/TablePro/utils";
import { normalizeAssetUpdatePayload } from "../components/TablePro/Table/utils";
import { useAssetsCollection } from "../contexts/assetsCollection";
import { ASSETS_BULK_UPDATE_MUTATION } from "../constants/graphql";
import useMutation from "./useMutation";
import useNotification from "./notification";
import { useUndo } from "../contexts/undo";
import { useBanner } from "../contexts/banner";

const getFieldValue = (asset, fieldName) => {
  const customFieldMatch = fieldName.match(/^customField(\d+)$/);
  if (customFieldMatch) {
    const templateId = customFieldMatch[1];
    const customField = asset.customFields?.find(
      cf => String(cf.customFieldTemplate?.id) === templateId
    );
    return customField?.value;
  }
  return asset[fieldName];
};

const useAssetsTable = () => {
  const { addAction } = useUndo();
  const { notifyError } = useNotification();
  const { displayBanner } = useBanner();
  const [bulkUpdateAssets] = useMutation(ASSETS_BULK_UPDATE_MUTATION);
  const { setCollection, collection, setLoadingMutation } = useAssetsCollection();
  const pendingUpdatesRef = React.useRef(new Set());

  const handleUpdate = (id, values, { skipMutation, localStateUpdate } = {}) => {
    if (skipMutation) {
      replaceRecord(setCollection, { id, values });
    } else {
      handleBulkUpdate(
        { [id]: values },
        {
          saveUndo: true,
          localStateVariables: localStateUpdate ? { [id]: localStateUpdate } : null,
        }
      );
    }
  };

  const handleBulkUpdate = (variables, options = {}) => {
    const { saveUndo = true, localStateVariables = null } = options;

    // Create unique key for this update to prevent duplicates
    const updateKey = JSON.stringify({ variables, options });

    // Check if this exact update is already in progress
    if (pendingUpdatesRef.current.has(updateKey)) {
      return; // Skip duplicate request
    }

    // Mark this update as pending
    pendingUpdatesRef.current.add(updateKey);

    if (saveUndo) {
      const previousState = Object.keys(variables).reduce((acc, id) => {
        const selected = collection.find(v => String(v.id) === id);
        if (selected) {
          const fields = Object.keys(variables[id]);
          acc[id] = fields.reduce((fieldAcc, field) => {
            fieldAcc[field] = getFieldValue(selected, field);
            return fieldAcc;
          }, {});
        }
        return acc;
      }, {});

      if (Object.keys(previousState).length > 0) {
        addAction([previousState, variables]);
      }
    }

    const assetCount = Object.keys(variables).length;
    if (assetCount > 5) {
      displayBanner({ message: "Processing...", type: "bulk-update-assets" });
    }

    setLoadingMutation(true);

    const stateUpdateVariables = localStateVariables || variables;
    replaceCollection(setCollection, stateUpdateVariables);

    const input = Object.keys(variables).reduce((result, id) => {
      const selected = collection.find(v => String(v.id) === id);
      const values = normalizeAssetUpdatePayload(variables[id], selected);

      // Ensure costCenterId is an integer for GraphQL
      if (values.costCenterId !== undefined) {
        values.costCenterId =
          values.costCenterId === null || values.costCenterId === ""
            ? null
            : parseInt(values.costCenterId);
      }

      return [...result, { id: parseInt(id), ...values }];
    }, []);

    bulkUpdateAssets({
      variables: { input },
      onSuccess: () => {
        pendingUpdatesRef.current.delete(updateKey);
        setLoadingMutation(false);
      },
      onFailure: errors => {
        pendingUpdatesRef.current.delete(updateKey); // Clear pending flag
        notifyError(errors);
        setCollection(collection);
        setLoadingMutation(false);
      },
    });
  };

  return {
    handleUpdate,
    handleBulkUpdate,
  };
};

export default useAssetsTable;
